import { useQuery } from "@tanstack/react-query";
import { Layout } from "providers/Layout";
import { Link, useParams, useSearchParams } from "react-router-dom";
import addressApi from "api/landlord/address";
import { LayoutHeader } from "views/applications-id/components/LayoutHeader";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { ActivityLog } from "../../shared/app/ActivityLog";
import { DecisionConfirmationModal } from "views/applications-id/components/DecisionConfirmationModal";
import { Application, Applicant } from "types/application";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { ApplicationStatus, VerificationNames } from "shared/types";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import { Template } from "types/landlord/verificationTemplate";
import { CancelOrRefundModal } from "shared/app/CancelOrRefundModal";
import { useAuthContext } from "providers/Auth";
import { Redirect } from "shared/components/Redirect";
import { Loader } from "shared/components/Loader";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import {
  generateQueries,
  getIncomeGraphsData
} from "./components/IncomeSummary/utils";
import { VerificationData } from "./components/IncomeSummary/types";
import { ApplicantStatus, ApplicantType } from "shared/types/application";
import { MakeDecisionModal } from "shared/app/MakeDecisionModal";
import { Card } from "shared/components/Card";
import { GroupStats } from "./components/GroupStats";
import { FaceID, Loading02 } from "untitledui-js-base";
import { Tabs } from "shared/components/Tabs";
import { ApplicantsTable } from "./components/ApplicantsTable";
import { MoveModal } from "../../shared/app/MoveModal";
import { RefundModal } from "shared/app/RefundModal";
import { ExplainRecommendationModal } from "shared/app/ExplainRecommendationModal";
import { CriteriaTypeOptions } from "shared/app/VerificationTemplate/slices/DecisionCriteria/constants";
import { canDecisionBeMade } from "utils/canMakeDecision";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { hasActionInManualApplication } from "./helpers";
import { UncancelModal } from "shared/app/UncancelModal";
import { HugeiconsIcon } from "@hugeicons/react";
import { PauseCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { SplitModal } from "shared/app/SplitModal";
import { ApplicationOverview } from "./slices/dashboard/ApplicationOverview";
import { DecisionCard } from "./slices/dashboard/DecisionCard";
import { LoadingSkeletonSlice } from "./slices/LoadingSkeletonSlice";
import { ApplicationIntelligenceOverview } from "./slices/application-intelligence";
import { FEATURES } from "types/landlord/partner";

const IncomeSummary = lazy(() => import("./components/IncomeSummary"));
type TabNames = "Applicants" | "Guarantors" | "Occupants";

export const ApplicationsIdOverview = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showUncancelModal, setShowUncancelModal] = useState<boolean>(false);
  const [showActivityLog, setShowActivityLog] = useState<boolean>(
    Boolean(searchParams.get("note_id"))
  );
  const { applicationId } = useParams();
  const [showDecisionFlowModal, setShowDecisionFlowModal] =
    useState<boolean>(false);
  const [showExplainRecommendations, setShowExplainRecommendations] =
    useState<boolean>(false);
  const [showDecisionConfirmationModal, setDecisionConfirmationModal] =
    useState<boolean>(false);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState<boolean>(false);
  const [hideApplicantNamesAndUnitInfo, setHideApplicantNamesAndUnitInfo] =
    useState(false);
  const [splitModalId, setSplitModalId] = useState<string | undefined>();

  const [previewTemplate, setPreviewTemplate] = useState<string>();

  const [tab, setActiveTab] = useState<TabNames>("Applicants");
  const [showMoveModal, setShowMoveModal] = useState<boolean>(false);

  const query = useQuery<Application>({
    queryKey: ["APPLICATION", applicationId],
    queryFn: () => addressApi.getApplicationById(applicationId || "")
  });

  const { type, permissions, partner } = useAuthContext();

  const {
    unit,
    property,
    applicants,
    status,
    submitted_at,
    verification_template,
    pending_magic_links,
    monthly_rent_amount,

    include_co_signer_in_criteria_calculation,
    requires_overrides_identity_verification,
    decision_at,
    rent_amount,
    home_rent_amount,
    site_rent_amount,
    housing_assistance_voucher_amount,
    pet_rent_amount,
    latest_application_rent_history
  } = query.data || {};

  const scoreBased =
      verification_template?.recommendation_method === "score_based",
    ruleBased = verification_template?.recommendation_method === "rule_based";

  const [applicantUsers, coSignerUsers, occupantUsers] = useMemo(() => {
    return (applicants || []).reduce<Applicant[][]>(
      (acc, applicant) => {
        switch (applicant.role) {
          case ApplicantType.CoSigner:
            acc[1].push(applicant);
            break;
          case ApplicantType.Occupant:
            acc[2].push(applicant);
            break;
          default:
            acc[0].push(applicant);
        }
        return acc;
      },
      [[], [], []]
    );
  }, [applicants]);

  const [applicantLinks, coSignerLinks, occupantLinks] = useMemo(() => {
    return (pending_magic_links || []).reduce<
      { email: string; phone: string | null; role: string }[][]
    >(
      (acc, link) => {
        switch (link.role) {
          case ApplicantType.CoSigner:
            acc[1].push(link);
            break;
          case ApplicantType.Occupant:
            acc[2].push(link);
            break;
          default:
            acc[0].push(link);
        }
        return acc;
      },
      [[], [], []]
    );
  }, [pending_magic_links]);

  const rolesEnabledInSettings = useMemo(() => {
    if (!verification_template?.settings) return [];

    return Object.keys(verification_template.settings).filter(
      (role) => verification_template.settings[role].enabled_screening_services
    ) as ApplicantType[];
  }, [verification_template]);

  const incomeQueries = useMemo(
    () =>
      generateQueries(
        applicants?.filter((appl) => {
          if (!include_co_signer_in_criteria_calculation)
            return (
              appl.role === ApplicantType.Applicant &&
              appl.application_status === ApplicantStatus.Submitted
            );
          else return appl.application_status === ApplicantStatus.Submitted;
        }) as Applicant[]
      ),
    [applicants, include_co_signer_in_criteria_calculation]
  );

  const combinedQueryMeta = useMemo(() => {
    return incomeQueries?.reduce(
      (acc, result) => {
        switch (result.verification.name) {
          case VerificationNames.VerifiedIncomeVerification:
            acc.verifiedIncomeData.push(result as VerificationData);
            break;
          case VerificationNames.IncomeVerification:
            acc.selfAttestedIncomeData.push(result as VerificationData);
            break;
          case VerificationNames.CreditReport:
            acc.creditScore.push(result as VerificationData);
            break;
        }
        return acc;
      },
      {
        verifiedIncomeData: [] as VerificationData[],
        selfAttestedIncomeData: [] as VerificationData[],
        creditScore: [] as VerificationData[]
      }
    );
  }, [incomeQueries]);

  const { verifiedIncomeGraphData } = useMemo(
    () => getIncomeGraphsData(combinedQueryMeta?.verifiedIncomeData),
    [combinedQueryMeta]
  );

  const canMakeDecision = useMemo(() => {
    return canDecisionBeMade({
      applicants: applicants || [],
      status: status || ApplicationStatus.Started,
      type,
      permissions,
      requirements: query.data?.make_decision_requirements
    });
  }, [
    requires_overrides_identity_verification,
    applicants,
    status,
    type,
    permissions
  ]);

  const availableTabs: TabNames[] = useMemo(() => {
    const res: TabNames[] = [];
    if (applicantLinks.length + applicantUsers.length > 0)
      res.push("Applicants");
    if (coSignerLinks.length + coSignerUsers.length > 0) res.push("Guarantors");
    if (occupantLinks.length + occupantUsers.length > 0) res.push("Occupants");

    return res.length > 0 ? res : [];
  }, [
    coSignerLinks,
    coSignerUsers,
    occupantLinks,
    occupantUsers,
    applicantLinks,
    applicantUsers
  ]);

  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(tab))
      setActiveTab(availableTabs[0]);
  }, [availableTabs]);

  const notCancelledApplicants = useMemo(() => {
    return applicants?.filter(
      (applicant) => applicant.application_status !== ApplicantStatus.Canceled
    );
  }, [applicants]);

  if (!type.isBoomScreen) return <Redirect href="/" />;

  if (query.isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading application
          </h1>

          <Link to="/applications">
            <Button>Go back</Button>
          </Link>
        </div>
      </Layout>
    );

  // handle loading state here
  if (query.isLoading) return <LoadingSkeletonSlice />;
  if (!unit || !property || !query.data) return null;

  const allowCoSigner = Boolean(
      // INFO: According to Dat it was true because before this field existed, we used to allow cosigners by default
      verification_template?.settings.co_signer.enabled_screening_services ??
        true
    ),
    allowOccupant = Boolean(
      verification_template?.settings.occupant?.enabled_screening_services ??
        false
    );

  const manualApplicantsInProgress = applicants?.filter((appl) =>
    hasActionInManualApplication(appl as Applicant)
  );

  return (
    <Layout
      sectionProps={{
        // Hide applicants' names and unit info in header when scroll down
        onScroll: (e) => {
          setHideApplicantNamesAndUnitInfo(e.currentTarget.scrollTop > 0);
        }
      }}
      sectionClassName="!p-0"
      headerClassName="z-[2] bg-white border-b border-alpha-100"
      header={
        <LayoutHeader
          application={query.data}
          hideApplicantNamesAndUnitInfo={hideApplicantNamesAndUnitInfo}
          disableDecisionFlow={!canMakeDecision}
          applicants={applicants}
          invitees={pending_magic_links || []}
          applicationId={applicationId || ""}
          viewTemplate={() => {
            setPreviewTemplate(verification_template?.id);
          }}
          requirements={query.data.make_decision_requirements}
          setShowActivityLog={setShowActivityLog}
          setShowDecisionFlowModal={setShowDecisionFlowModal}
          applicationStatus={status || ApplicationStatus.Started}
          setDecisionConfirmationModal={setDecisionConfirmationModal}
          submittedAt={submitted_at || ""}
          setShowInviteApplicantsModal={setShowInviteApplicantsModal}
          onCancelClick={() => setShowCancelModal(true)}
          onUncancelClick={() => setShowUncancelModal(true)}
          onRefundClick={() => setShowRefundModal(true)}
          onMoveClick={() => setShowMoveModal(true)}
          onSplitClick={() => setSplitModalId(applicationId)}
          decisionAt={decision_at || ""}
        />
      }
    >
      <div className="space-y-6 p-6 pb-0">
        <div className="flex gap-4 lg:flex-nowrap flex-wrap">
          {verification_template && applicants ? (
            <DecisionCard
              wrapperClassName="[@media(min-width:1695px)]:hidden w-full max-w-[300px] shrink-0"
              score={query.data.score}
              setShowExplainRecommendations={setShowExplainRecommendations}
              setShowDecisionFlowModal={(v) => {
                if (v)
                  status === ApplicationStatus.Submitted
                    ? setShowDecisionFlowModal(true)
                    : setDecisionConfirmationModal(true);
                else {
                  setShowDecisionFlowModal(false);
                  setDecisionConfirmationModal(false);
                }
              }}
              recommendationMethod={verification_template.recommendation_method}
              requiresOverridesIdentityVerification={
                requires_overrides_identity_verification
              }
              applicants={applicants}
              canMakeDecision={canMakeDecision}
              ruleReasons={query.data.rule_reasons}
              autoApproved={query.data.auto_approved}
              criteriaCheckSettings={query.data.criteria_check_settings}
              screeningCriteria={verification_template.screening_criteria}
              applicationStatus={query.data.status}
              recommendation={query.data.recommendation}
              hasPendingVerifications={query.data.has_pending_verifications}
            />
          ) : null}

          <ApplicationOverview
            applicationId={String(applicationId)}
            applicationRentAmount={{
              monthly_rent_amount,
              rent_amount,
              home_rent_amount,
              site_rent_amount,
              housing_assistance_voucher_amount,
              pet_rent_amount
            }}
            setShowActivityLog={setShowActivityLog}
            applicationStatus={status || ApplicationStatus.Started}
            latestApplicationRentHistory={latest_application_rent_history}
            unit={unit}
            property={property}
            desiredMoveInDate={query.data.desired_move_in_date}
          />
        </div>
        {partner?.features.includes(FEATURES.AI_APPLICATION_SUMMARY) ? (
          <ApplicationIntelligenceOverview
            applicationId={Number(applicationId)}
            applicants={(applicants || []) as Applicant[]}
            hasSubmittedApplicants={applicants?.some(
              (appl) => appl.application_status === ApplicantStatus.Submitted
            )}
          />
        ) : null}

        <Show
          if={
            !Boolean(
              requires_overrides_identity_verification?.applicants.length
            ) &&
            [
              ApplicationStatus.Submitted,
              ApplicationStatus.Approved,
              ApplicationStatus.Rejected,
              ApplicationStatus.ConditionalApproved,
              ApplicationStatus.Canceled
            ].includes(status as ApplicationStatus)
          }
        >
          <div className="flex gap-x-4 gap-y-6 flex-wrap lg:flex-nowrap">
            {verification_template && applicants ? (
              <DecisionCard
                wrapperClassName="[@media(min-width:1695px)]:block hidden max-w-[300px] w-full"
                score={query.data.score}
                setShowExplainRecommendations={setShowExplainRecommendations}
                setShowDecisionFlowModal={(v) => {
                  if (v)
                    status === ApplicationStatus.Submitted
                      ? setShowDecisionFlowModal(true)
                      : setDecisionConfirmationModal(true);
                  else {
                    setShowDecisionFlowModal(false);
                    setDecisionConfirmationModal(false);
                  }
                }}
                recommendationMethod={
                  verification_template.recommendation_method
                }
                requiresOverridesIdentityVerification={
                  requires_overrides_identity_verification
                }
                applicants={applicants}
                canMakeDecision={canMakeDecision}
                ruleReasons={query.data.rule_reasons}
                autoApproved={query.data.auto_approved}
                criteriaCheckSettings={query.data.criteria_check_settings}
                screeningCriteria={verification_template.screening_criteria}
                applicationStatus={query.data.status}
                recommendation={query.data.recommendation}
                hasPendingVerifications={query.data.has_pending_verifications}
              />
            ) : null}

            <GroupStats
              criteriaTypeOptions={
                query.data.criteria_check_settings?.credit_score?.type ||
                CriteriaTypeOptions.AVERAGE
              }
              criteriaSettings={
                verification_template?.screening_criteria?.applicant_settings
              }
              recommendationMethod={
                verification_template?.recommendation_method
              }
              groupStat={query.data.group_stat}
              includeCoSignerInCriteriaCalculation={
                include_co_signer_in_criteria_calculation
              }
              applicationId={applicationId || ""}
              applicants={applicants}
              isRuleBased={ruleBased}
            />
          </div>

          <Show if={permissions.canAccessReports.incomeReport}>
            <Suspense
              fallback={
                <Card className="flex items-center justify-center gap-2 h-[106px] mb-6 px-4 bg-white">
                  <Loader />
                </Card>
              }
            >
              <IncomeSummary
                verifiedIncomeGraphData={verifiedIncomeGraphData}
                isLoading={query.isLoading}
                isError={query.isError}
              />
            </Suspense>
          </Show>
        </Show>
        <Show
          if={Boolean(
            requires_overrides_identity_verification?.applicants.length
          )}
        >
          <Card className="flex items-center gap-2 py-6 px-4">
            <div className="relative m-4">
              <FaceID
                className="size-10 shrink-0 stroke-content-200"
                strokeWidth={1.5}
              />
              <InfoIcon className="absolute -bottom-1.5 -right-1.5 size-6 text-danger-500 bg-white rounded-full" />
            </div>
            <div className="space-y-2">
              <h2 className="text-base font-bold">
                {`Identity verification failed for ${requires_overrides_identity_verification?.applicants
                  .map((applicant) => applicant.name)
                  .join(", ")}`}
              </h2>
              <p className="text-sm">
                You can manually override a failed identity verification run the
                remaining screening services
              </p>
            </div>
          </Card>
        </Show>
        <Show if={Boolean(status === ApplicationStatus.OnHold)}>
          <Card className="flex items-center gap-6 py-8 px-6">
            <HugeiconsIcon
              icon={PauseCircleIcon}
              size={50}
              className="text-content-1000 shrink-0"
              strokeWidth={1.5}
            />
            <div className="space-y-1">
              <h2 className="text-base font-medium text-content-1000">
                Application is on hold
              </h2>
              <p className="text-sm text-content-300 font-medium">
                Application is on hold for now and can be resumed whenever
                you&apos;re ready
              </p>
            </div>
          </Card>
        </Show>
        <Show
          if={Boolean(
            [
              ApplicationStatus.Pending,
              ApplicationStatus.Started,
              undefined
            ].includes(status)
          )}
        >
          {manualApplicantsInProgress?.length ? (
            <Card className="h-[110px] mb-6 px-6 py-8 flex gap-6 items-center">
              <div className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center bg-primary-50">
                <span className="text-base text-primary-500 font-bold">
                  {manualApplicantsInProgress.length}
                </span>
              </div>
              <div>
                <h2 className="text-base font-medium text-content-1000 mb-1">
                  Application{manualApplicantsInProgress.length > 1 ? "s" : ""}{" "}
                  require manual completion{" "}
                </h2>
                <p className="text-sm text-content-300">
                  All in-progress applications must be completed in order to
                  make a decision
                </p>
              </div>

              <Button
                variant={ButtonVariant.PRIMARY}
                className="ml-auto"
                size={ButtonSize.SM}
                onClick={() => {
                  setSearchParams({
                    applicantId: manualApplicantsInProgress[0].id.toString()
                  });
                }}
              >
                Complete application
              </Button>
            </Card>
          ) : (
            <Card className="h-[110px] mb-6 px-6 py-8 flex gap-6 items-center">
              <Loading02
                strokeWidth={1}
                className="h-12 w-12 shrink-0 stroke-content-300"
              />
              <div>
                <h2 className="text-base font-medium text-content-1000 mb-1">
                  Application is in progress
                </h2>
                <p className="text-sm text-content-300">
                  All in-progress applications must be completed in order to
                  make a decision
                </p>
              </div>
            </Card>
          )}
        </Show>
        <Show if={availableTabs.length > 0}>
          <div className="mb-4">
            <Tabs
              tabs={availableTabs}
              displayMap={{
                Applicants: (
                  <>
                    Applicants ({applicantLinks.length + applicantUsers.length})
                  </>
                ),
                Guarantors: (
                  <>
                    Guarantors ({coSignerLinks.length + coSignerUsers.length})
                  </>
                ),
                Occupants: (
                  <>Occupants ({occupantLinks.length + occupantUsers.length})</>
                )
              }}
              selected={tab}
              setActiveTab={(tab: TabNames) => setActiveTab(tab)}
              variant="underline"
            />
          </div>
        </Show>
        <Show if={tab === "Applicants"}>
          <ApplicantsTable
            pendingMagicLinks={pending_magic_links || []}
            key="Applicants"
            applicationApplicants={notCancelledApplicants || []}
            availableRoles={rolesEnabledInSettings}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            applicationStatus={status}
            ruleBased={ruleBased}
            scoreBased={scoreBased}
            applicationId={applicationId}
            templateId={verification_template?.id}
            verificationSchemas={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              verification_template?.verification_schemas?.[
                ApplicantType.Applicant
              ]
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            applicants={(applicantUsers || []).concat(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              (applicantLinks || []) as Applicant[]
            )}
            invalidateQueries={["APPLICATION", applicationId]}
            onChangeRole={() => setActiveTab("Applicants")}
            requiresOverridesIdentityVerification={
              requires_overrides_identity_verification
            }
          />
        </Show>

        <Show if={tab === "Guarantors"}>
          <ApplicantsTable
            pendingMagicLinks={pending_magic_links || []}
            applicationApplicants={notCancelledApplicants || []}
            key="Guarantors"
            availableRoles={rolesEnabledInSettings}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            applicationStatus={status}
            ruleBased={ruleBased}
            applicationId={applicationId}
            templateId={verification_template?.id}
            scoreBased={scoreBased}
            verificationSchemas={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              verification_template?.verification_schemas?.[
                ApplicantType.CoSigner
              ]
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            applicants={(coSignerUsers || []).concat(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              (coSignerLinks || []) as Applicant[]
            )}
            invalidateQueries={["APPLICATION", applicationId]}
            onChangeRole={() => setActiveTab("Applicants")}
            requiresOverridesIdentityVerification={
              requires_overrides_identity_verification
            }
          />
        </Show>
        <Show if={tab === "Occupants"}>
          <ApplicantsTable
            pendingMagicLinks={pending_magic_links || []}
            applicationApplicants={notCancelledApplicants || []}
            key="Occupants"
            availableRoles={rolesEnabledInSettings}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            applicationStatus={status}
            ruleBased={ruleBased}
            applicationId={applicationId}
            templateId={verification_template?.id}
            scoreBased={scoreBased}
            verificationSchemas={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              verification_template?.verification_schemas?.[
                ApplicantType.Occupant
              ]
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            applicants={(occupantUsers || []).concat(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              (occupantLinks || []) as Applicant[]
            )}
            invalidateQueries={["APPLICATION", applicationId]}
            onChangeRole={() => setActiveTab("Applicants")}
            requiresOverridesIdentityVerification={
              requires_overrides_identity_verification
            }
          />
        </Show>
      </div>
      <TemplateReviewModal
        opened={!!previewTemplate}
        onClose={() => setPreviewTemplate(undefined)}
        template={verification_template as unknown as Template}
      />
      {/* Show removes unnecessary fetch request */}
      <Show if={showActivityLog}>
        <ActivityLog
          propertyName={property.name}
          showActivityLog={showActivityLog}
          setShowActivityLog={setShowActivityLog}
          applicationId={applicationId || ""}
        />
      </Show>
      <Show if={showDecisionFlowModal}>
        <MakeDecisionModal
          opened={showDecisionFlowModal}
          onClose={() => setShowDecisionFlowModal(false)}
          applicationId={applicationId || ""}
        />
      </Show>
      <ExplainRecommendationModal
        application={query.data}
        opened={showExplainRecommendations}
        onClose={() => setShowExplainRecommendations(false)}
      />
      <DecisionConfirmationModal
        opened={showDecisionConfirmationModal}
        onContinue={() => {
          setDecisionConfirmationModal(false);
          setShowDecisionFlowModal(true);
        }}
        onClose={() => setDecisionConfirmationModal(false)}
      />
      <Show if={showInviteApplicantsModal}>
        <InviteAndCreateModal
          disabledRoles={
            [...(applicants || []), ...(pending_magic_links || [])].some(
              (applicant) =>
                [ApplicantType.Applicant, ApplicantType.Occupant].includes(
                  applicant.role as ApplicantType
                )
            )
              ? []
              : [ApplicantType.CoSigner]
          }
          open={showInviteApplicantsModal}
          setOpen={() => setShowInviteApplicantsModal(false)}
          applicationId={Number(applicationId)}
          propertyId={property.id}
          propertyName={property.name}
          unitId={unit.id}
          unitName={unit.title}
          onInviteRefreshKey={["APPLICATION", applicationId]}
        />
      </Show>
      <Show if={Boolean(splitModalId)}>
        <SplitModal
          isOpen={Boolean(splitModalId)}
          applicationId={splitModalId ?? ""}
          onClose={() => setSplitModalId(undefined)}
        />
      </Show>
      <CancelOrRefundModal
        applicants={applicants}
        applicationId={showCancelModal ? String(applicationId) : ""}
        onClose={() => setShowCancelModal(false)}
        onSuccess={() => {
          query.refetch();
        }}
      />
      <UncancelModal
        applicationId={showUncancelModal ? String(applicationId) : ""}
        onClose={() => setShowUncancelModal(false)}
        onSuccess={() => {
          query.refetch();
        }}
      />
      <RefundModal
        applicationId={showRefundModal ? String(applicationId) : ""}
        onClose={() => setShowRefundModal(false)}
        queryKey={["APPLICATION", applicationId]}
      />
      <MoveModal
        applicationId={showMoveModal ? String(applicationId) : ""}
        onClose={() => setShowMoveModal(false)}
      />
    </Layout>
  );
};
